// React
import { useMemo } from "react"

// Icons
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "@/lib/icons"

// Dates
import { DateTime } from "@/lib/dates"

// Translate
import { useTrans } from "@/i18n"

// Context
import { useAreaGraphContext } from "./AreaGraph"

// UI
import { getTransitionPopoverProps } from "@/lib/animations"
import {
	Popover,
	PopoverGroup,
	PopoverButton,
	PopoverPanel,
	Transition,
} from "@/lib/ui"
import { Tooltip } from "@/components/Tooltip"
import { Button } from "@/components/Button"
import { DatePicker } from "@/components/form-controls/DatePicker"

/**
 * AreaGraphActions
 * @returns
 */
export function AreaGraphActions() {
	const { maximumDate, minimumDate, date, setDate } = useAreaGraphContext()
	const t = useTrans(["project", "common"])

	const currentDateTime = DateTime.fromJSDate(date)

	// is currentDateTime (current selected date) before now?
	const canGoNext = currentDateTime < maximumDate

	// is minimumDate before current date time
	const canGoPrev = currentDateTime > minimumDate

	return (
		<>
			{/* prev day button */}
			<Button
				onClick={() => {
					if (canGoPrev) {
						const nextDate = currentDateTime.minus({
							days: 1,
						})
						setDate(nextDate.toJSDate())
					}
				}}
				disabled={!canGoPrev}
				className="group relative rounded-r-none focus:z-20"
				variant="transparent"
			>
				<ChevronLeftIcon className="size-5 shrink-0 text-gray-400 group-hover:text-gray-500" />
			</Button>

			{/* date picker */}
			<Tooltip
				content={
					"we do not store daily information for days more than a week old"
				}
			>
				<PopoverGroup className="flex items-center divide-x divide-gray-200">
					<Popover className="relative z-10 inline-block text-right">
						<PopoverButton
							as="div"
							className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900"
						>
							<Button
								variant="transparent"
								size="small"
								className="relative rounded-none border-l-0 focus:z-20"
							>
								<span className="mr-2">
									{DateTime.fromJSDate(date).toFormat(
										t(
											"project:project.data.production_today.date_picker.title.date_format",
										),
									)}
								</span>

								<ChevronDownIcon
									className="size-5 shrink-0 text-gray-400 group-hover:text-gray-500"
									aria-hidden="true"
								/>
							</Button>
						</PopoverButton>
						<Transition {...getTransitionPopoverProps()}>
							<PopoverPanel className="absolute left-0 mt-2 origin-top-right lg:left-auto lg:right-0">
								<div
									className="min-w-[300px]"
									data-testid="daterangepicker-from"
								>
									<DatePicker
										initialMonth={currentDateTime.month}
										initialYear={currentDateTime.year}
										selectedDate={currentDateTime}
										showChangeOfYear={false}
										showNavigation={false}
										months={useMemo(() => {
											if (
												maximumDate.month !==
												minimumDate.month
											) {
												return [
													minimumDate.month,
													maximumDate.month,
												]
											}
											return [maximumDate.month]
										}, [
											maximumDate.month,
											minimumDate.month,
										])}
										onSelectDate={(date) => {
											const newDate =
												DateTime.fromObject(date).endOf(
													"day",
												)
											setDate(newDate.toJSDate())
										}}
										isDateDisabled={(date) => {
											const dateTime =
												DateTime.fromObject({
													...date,
													day: date.day + 1,
												})

											// is this date within our range?
											if (
												dateTime < maximumDate &&
												dateTime >
													minimumDate.minus({
														days: 1,
													})
											) {
												return false
											}

											return true
										}}
									/>
								</div>
							</PopoverPanel>
						</Transition>
					</Popover>
				</PopoverGroup>
			</Tooltip>
			{/* next day button */}
			<Button
				onClick={() => {
					if (canGoNext) {
						const nextDate = currentDateTime.plus({
							days: 1,
						})
						setDate(nextDate.toJSDate())
					}
				}}
				className="group relative rounded-l-none focus:z-20"
				variant="transparent"
				disabled={!canGoNext}
			>
				<ChevronRightIcon className="size-5 shrink-0 text-gray-400 group-hover:text-gray-500" />
			</Button>
		</>
	)
}
